import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient, httpResource } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { formatCurrencyWithSymbol } from '../utils/currency.utils';
import { AuthService } from './auth.service';

export interface ImageCropData {
  x: number;
  y: number;
  width: number;
  height: number;
  zoom?: number;
  rotation?: number;
}

export interface VoucherProductVersion {
  version_id: string;
  version_number: number;
  name: string;
  description: string | null;
  price: number; // Price in cents
  discount_percentage: number; // 0-50, customer pays reduced price
  image_path: string | null;
  image_cropped_path: string | null;
  is_current: boolean;
  created_at: string;
  change_summary: string | null;
  purchase_count: number;
}

export interface VoucherProduct {
  public_id: string;
  public_company_id: string;
  name: string;
  description: string | null;
  price: number; // Price in cents
  discount_percentage: number; // 0-50, customer pays reduced price
  current_version_id: string | null;
  version_count: number;
  purchase_count: number; // Total number of vouchers purchased for this product
  image_path: string | null; // Original image (for re-editing)
  image_cropped_path: string | null; // Cropped square image (for display)
  image_crop_data: ImageCropData | null; // Crop settings for re-editing
  is_active: boolean;
  is_archived: boolean;
  archived_at: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface VoucherProductCreate {
  name: string;
  description?: string;
  price: number; // Price in cents
  discount_percentage?: number; // 0-50
  is_active?: boolean;
}

export interface VoucherProductUpdate {
  name?: string;
  description?: string;
  price?: number; // Price in cents
  discount_percentage?: number; // 0-50
  is_active?: boolean;
}

export interface ImageUploadResponse {
  filename: string;
  file_path: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class VoucherProductService {
  private apiUrl = `${environment.apiUrl}/voucher-products`;
  private readonly auth = inject(AuthService);

  /**
   * The product list as a resource.
   *
   * Replaces the hand-rolled load/cache/in-flight bookkeeping: the request is
   * declared once, runs when the user is authenticated, and exposes value,
   * loading and error as signals. The URL returns undefined while logged out,
   * which keeps the resource idle instead of firing an anonymous request.
   */
  private readonly productsResource = httpResource<VoucherProduct[]>(
    () => (this.auth.isAuthenticated() ? this.apiUrl : undefined),
    { defaultValue: [] },
  );

  private readonly _archivedProducts = signal<VoucherProduct[]>([]);

  readonly products = this.productsResource.value;
  readonly loading = this.productsResource.isLoading;
  readonly error = computed(() =>
    this.productsResource.error() ? 'Produkte konnten nicht geladen werden.' : null,
  );
  readonly archivedProducts = this._archivedProducts.asReadonly();

  /**
   * @deprecated Compatibility bridge for components that still subscribe.
   * Read the `products` signal instead.
   */
  public products$ = toObservable(this.products);
  /** @deprecated See {@link products$}. */
  public archivedProducts$ = toObservable(this._archivedProducts);

  constructor(private http: HttpClient) {}

  /**
   * Re-read the product list.
   *
   * The resource loads itself once the user is authenticated, so this is only
   * needed after a mutation. It replaces the old `loadProducts()` that returned
   * an Observable — five call sites each fired their own GET for the same list.
   */
  refresh(): void {
    this.productsResource.reload();
  }

  /**
   * Load archived voucher products for the authenticated company
   */
  loadArchivedProducts(): Observable<VoucherProduct[]> {
    return this.http
      .get<VoucherProduct[]>(`${this.apiUrl}/archived`)
      .pipe(tap((products) => this._archivedProducts.set(products)));
  }

  /**
   * Get a single voucher product by ID
   */
  getProduct(publicId: string): Observable<VoucherProduct> {
    return this.http.get<VoucherProduct>(`${this.apiUrl}/${publicId}`);
  }

  /**
   * Create a new voucher product
   */
  createProduct(productData: VoucherProductCreate): Observable<VoucherProduct> {
    return this.http.post<VoucherProduct>(this.apiUrl, productData).pipe(
      tap(() => this.refresh()), // Refresh list
    );
  }

  /**
   * Update an existing voucher product
   */
  updateProduct(publicId: string, productData: VoucherProductUpdate): Observable<VoucherProduct> {
    return this.http.patch<VoucherProduct>(`${this.apiUrl}/${publicId}`, productData).pipe(
      tap(() => this.refresh()), // Refresh list
    );
  }

  /**
   * Apply a single discount (0-50%) to all of the company's products.
   * Passing 0 removes all discounts.
   */
  setDiscountForAll(
    discountPercentage: number,
  ): Observable<{ message: string; updated_count: number; discount_percentage: number }> {
    return this.http
      .post<{
        message: string;
        updated_count: number;
        discount_percentage: number;
      }>(`${this.apiUrl}/bulk-discount`, { discount_percentage: discountPercentage })
      .pipe(tap(() => this.refresh())); // Refresh list
  }

  /**
   * Archive a voucher product (soft delete)
   */
  archiveProduct(publicId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${publicId}`).pipe(
      tap(() => {
        this.refresh(); // Refresh active list
        this.loadArchivedProducts().subscribe(); // Refresh archived list
      }),
    );
  }

  /**
   * Unarchive a voucher product (restore)
   */
  unarchiveProduct(publicId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${publicId}/unarchive`, {}).pipe(
      tap(() => {
        this.refresh(); // Refresh active list
        this.loadArchivedProducts().subscribe(); // Refresh archived list
      }),
    );
  }

  /**
   * Upload an image for a voucher product
   */
  uploadProductImage(
    publicId: string,
    file: File,
    croppedFile?: File,
    cropData?: ImageCropData,
  ): Observable<ImageUploadResponse> {
    const formData = new FormData();
    formData.append('file', file); // Original file

    // Add cropped file if provided
    if (croppedFile) {
      formData.append('cropped_file', croppedFile);
    }

    // Add crop data if provided
    if (cropData) {
      formData.append('crop_data', JSON.stringify(cropData));
    }

    return this.http
      .post<ImageUploadResponse>(`${this.apiUrl}/${publicId}/upload-image`, formData)
      .pipe(
        tap(() => {
          // Force a fresh reload from the server to get updated timestamps
          this.refresh();
        }),
      );
  }

  /**
   * Delete the image for a voucher product
   */
  deleteProductImage(publicId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${publicId}/delete-image`).pipe(
      tap(() => this.refresh()), // Refresh list
    );
  }

  /**
   * Get the full URL for a product image with cache-busting
   * @param imagePath Path to the image
   * @param updatedAt Optional updated_at timestamp from product to use for cache-busting
   */
  getImageUrl(imagePath: string | null, updatedAt?: string): string | null {
    if (!imagePath) return null;

    // Use product's updated_at timestamp for cache-busting
    // This ensures same image is served from cache unless product is updated
    if (updatedAt) {
      const timestamp = new Date(updatedAt).getTime();
      return `${environment.apiUrl}/images/${imagePath}?v=${timestamp}`;
    }

    return `${environment.apiUrl}/images/${imagePath}`;
  }

  /**
   * Format price from cents in the company's currency ("49,99 €", "CHF 49.99")
   */
  formatPrice(priceInCents: number): string {
    return formatCurrencyWithSymbol(priceInCents);
  }

  /**
   * Reorder products
   */
  reorderProducts(productOrders: { public_id: string; display_order: number }[]): Observable<any> {
    return this.http.put(`${this.apiUrl}/reorder`, productOrders).pipe(
      tap(() => {
        // Reload products after reordering
        this.refresh();
      }),
    );
  }

  /**
   * Get version history for a product
   */
  getVersionHistory(publicId: string): Observable<VoucherProductVersion[]> {
    return this.http.get<VoucherProductVersion[]>(`${this.apiUrl}/${publicId}/versions`);
  }

  /**
   * Get a specific version of a product
   */
  getVersion(publicId: string, versionNumber: number): Observable<VoucherProductVersion> {
    return this.http.get<VoucherProductVersion>(
      `${this.apiUrl}/${publicId}/versions/${versionNumber}`,
    );
  }

  /**
   * Format version change summary for display
   */
  formatChangeSummary(summary: string | null): string {
    if (!summary) return 'No changes recorded';
    return summary;
  }

  /**
   * Get version badge color based on whether it's current
   */
  getVersionBadgeClass(isCurrent: boolean): string {
    return isCurrent ? 'badge-success' : 'badge-secondary';
  }
}
